import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../store/index.ts";
import {
  fetchAdminUnreadCount,
  fetchMyAdminNotifications,
  markAdminNotificationRead,
  markAllAdminNotificationsRead,
  AdminNotification,
} from "../../services/adminNotification.service.ts";
import {
  markAdminNotificationReadLocal,
  markAllAdminNotificationsReadLocal,
  setAdminUnreadCount,
} from "../../store/slices/adminNotificationsSlice.ts";
import {
  formatShortDate,
  getModuleIcon,
  getModuleLabel,
  getModuleTone,
} from "../../pages/my-notifications/notificationUi.ts";
import "./NotificationBell.css";

const POLL_MS = 60_000;
const DROPDOWN_LIMIT = 8;

function badgeLabel(count: number) {
  if (count <= 0) return null;
  if (count > 99) return "99+";
  return String(count);
}

const NotificationBell: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.auth.token);
  const unreadCount = useSelector(
    (state: RootState) => state.adminNotifications.unreadCount
  );

  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [items, setItems] = React.useState<AdminNotification[]>([]);
  const rootRef = React.useRef<HTMLLIElement | null>(null);

  const refreshUnread = React.useCallback(async () => {
    if (!token) return;
    try {
      const { count } = await fetchAdminUnreadCount();
      dispatch(setAdminUnreadCount(count));
    } catch {
      /* ignore poll errors */
    }
  }, [dispatch, token]);

  const loadRecent = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const page = await fetchMyAdminNotifications(DROPDOWN_LIMIT, 0);
      setItems(page.items);
      dispatch(setAdminUnreadCount(page.unreadCount));
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [dispatch, token]);

  React.useEffect(() => {
    if (!token) {
      setItems([]);
      setOpen(false);
      return;
    }
    refreshUnread();
    const id = window.setInterval(refreshUnread, POLL_MS);
    return () => window.clearInterval(id);
  }, [token, refreshUnread]);

  React.useEffect(() => {
    if (!open) return;

    const onClickOutside = (event: MouseEvent) => {
      if (
        rootRef.current &&
        !rootRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const handleToggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      await loadRecent();
    }
  };

  const handleMarkAll = async () => {
    try {
      await markAllAdminNotificationsRead();
      dispatch(markAllAdminNotificationsReadLocal());
      setItems((prev) => prev.map((item) => ({ ...item, isRead: true })));
      dispatch(setAdminUnreadCount(0));
    } catch {
      /* ignore */
    }
  };

  const handleItemClick = async (item: AdminNotification) => {
    try {
      if (!item.isRead) {
        await markAdminNotificationRead(item.id);
        dispatch(markAdminNotificationReadLocal(item.id));
        setItems((prev) =>
          prev.map((n) => (n.id === item.id ? { ...n, isRead: true } : n))
        );
        await refreshUnread();
      }
    } catch {
      /* still try navigate */
    }

    setOpen(false);
    if (item.link) {
      navigate(item.link);
    }
  };

  const label = badgeLabel(unreadCount);

  return (
    <li
      className="nav-item dropdown"
      ref={rootRef}
      style={{ listStyle: "none" }}
    >
      <a
        href="#/"
        className="nav-link notif-bell-trigger"
        onClick={(e) => {
          e.preventDefault();
          handleToggle();
        }}
        aria-label={
          unreadCount > 0
            ? `Notificaciones, ${unreadCount} sin leer`
            : "Notificaciones"
        }
        aria-expanded={open}
        aria-haspopup="true"
      >
        <i className="fa fa-bell notif-bell-trigger__icon" aria-hidden />
        {label && (
          <span className="notif-bell-badge" aria-hidden>
            {label}
          </span>
        )}
      </a>

      {open && (
        <div
          className="dropdown-menu dropdown-menu-end show notif-bell-menu"
          style={{ display: "block" }}
          role="menu"
          aria-label="Lista de notificaciones"
        >
          <div className="notif-bell-header">
            <div className="notif-bell-header__row">
              <h6 className="notif-bell-header__title">Notificaciones</h6>
              {unreadCount > 0 && (
                <span className="notif-bell-header__count">
                  {unreadCount} sin leer
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                className="btn btn-link btn-sm text-decoration-none notif-bell-header__action"
                onClick={(e) => {
                  e.preventDefault();
                  handleMarkAll();
                }}
                aria-label="Marcar todas como leídas"
              >
                Marcar todas como leídas
              </button>
            )}
          </div>

          {loading && (
            <div className="notif-bell-loading" role="status">
              <i className="fa-solid fa-spinner fa-spin me-2" aria-hidden />
              Cargando...
            </div>
          )}

          {!loading && items.length === 0 && (
            <div className="notif-bell-empty">No hay notificaciones</div>
          )}

          {!loading &&
            items.map((item) => {
              const tone = getModuleTone(item.module);
              return (
                <a
                  key={item.id}
                  href="#/"
                  role="menuitem"
                  className={`notif-bell-item ${
                    item.isRead
                      ? "notif-bell-item--read"
                      : "notif-bell-item--unread"
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleItemClick(item);
                  }}
                >
                  {item.isRead ? (
                    <span
                      className="notif-bell-item__dot-spacer"
                      aria-hidden
                    />
                  ) : (
                    <span className="notif-bell-item__dot" aria-hidden />
                  )}
                  <span
                    className={`notif-bell-item__icon notif-bell-item__icon--${tone}`}
                    aria-hidden
                  >
                    <i className={getModuleIcon(item.module)} />
                  </span>
                  <span className="notif-bell-item__body">
                    <span className="notif-bell-item__title">{item.title}</span>
                    <span className="notif-bell-item__meta">
                      {getModuleLabel(item.module)}
                      {" · "}
                      {formatShortDate(item.createdAt)}
                    </span>
                  </span>
                </a>
              );
            })}

          <div className="notif-bell-footer">
            <a
              href="#/"
              onClick={(e) => {
                e.preventDefault();
                setOpen(false);
                navigate("/mis-notificaciones");
              }}
            >
              Ver todas las notificaciones
            </a>
          </div>
        </div>
      )}
    </li>
  );
};

export default NotificationBell;
